// THE EDITOR OPENS THE BOOK THE WAY THE PRINTER BINDS IT (design system v2 §7
// S1, owner 2026-10-14: « what the client sees in the editor is EXACTLY what
// prints »).
//
//   plain book    spreads = album pages (1, 2), (3, 4)…   — behind the intro
//   themed book   spreads = (—, 1), (2, 3), (4, 5)…       — S1 alone on the right
//
// The pairing itself is bookStructure.ts (spreadCount / spreadPages / spreadOf,
// shared with the worker's page checker and the flow's printedSpreads). This
// file only turns it into what the editor writes on screen — the toolbar
// counter, the strip labels, the « + » insert point, the strip's drag — so
// AlbumEditorPage.tsx carries no `spread * 2` of its own.
//
// ⚠️ A PLAIN BOOK MUST NOT MOVE BY ONE CHARACTER. Every function below returns
// for `themed = false` exactly what the editor computed by hand before
// (editorSpreads.test.ts compares them with the old formulas, page count by
// page count) — the odd « 5–5 » on an odd last page included.
//
// Pure. No React.

import { spreadCount, spreadPages, spreadOf, spreadFirstIndex } from './bookStructure';
import { printPageOf, printSpreadOf, printedInteriorTotal } from './printPageNumbers';

export { spreadCount, spreadPages, spreadOf };

/** The last spread index of an album of `total` pages (0 when empty). */
export function lastSpread(total: number, themed: boolean): number {
  return Math.max(0, spreadCount(total, themed) - 1);
}

/** Clamp a spread index into the album. */
export function clampSpread(spread: number, total: number, themed: boolean): number {
  return Math.max(0, Math.min(Math.floor(spread) || 0, lastSpread(total, themed)));
}

/** The album pages (1-based positions) open on `spread` — only those that exist. */
export function spreadAlbumPages(spread: number, themed: boolean, total: number): number[] {
  return spreadPages(spread, themed, total).filter((i): i is number => i != null).map((i) => i + 1);
}

const range = (n: number[]): string => (n.length === 0 ? '' : n.length === 1 || n[0] === n[n.length - 1] ? `${n[0]}` : `${n[0]}–${n[n.length - 1]}`);

/** The customer's own page range on the toolbar (« 3–4 », themed opening « 1 »). */
export function spreadPagesLabel(spread: number, themed: boolean, total: number): string {
  if (!themed) return `${spread * 2 + 1}–${Math.min(spread * 2 + 2, total)}`;
  return range(spreadAlbumPages(spread, true, total));
}

/** The same spread in the PRINTED book and the 3D preview (« impr. 4–5 »). */
export function spreadPrintLabel(spread: number, themed: boolean, total: number, hasIntro: boolean, hasOutro: boolean): string {
  if (!themed) {
    const [l, r] = printSpreadOf(spread * 2 + 1, total, hasIntro, hasOutro);
    return `${l}–${r}`;
  }
  const printed = printedInteriorTotal(total, hasIntro, hasOutro);
  return range(spreadAlbumPages(spread, true, total).map((n) => Math.min(printPageOf(n, hasIntro), printed)));
}

/**
 * The label under a spread of the bottom strip. A plain book keeps its fixed
 * `p.{2s+2}–{2s+3}` (the intro thumbnail is p.1); a themed book has no intro,
 * so its album page N is p.N and only pages that exist are named.
 */
export function stripSpreadLabel(spread: number, themed: boolean, total: number): string {
  if (!themed) return `p.${spread * 2 + 2}–${spread * 2 + 3}`;
  return `p.${range(spreadAlbumPages(spread, true, total))}`;
}

/** The outro thumbnail's page number: right after the last album page. */
export function stripOutroLabel(themed: boolean, total: number): string {
  return themed ? `p.${total + 1}` : `p.${Math.ceil(total / 2) * 2 + 2}`;
}

/**
 * The page-array index the strip's « + » after spread `spread` inserts after:
 * the spread's right side, so a pair of new pages becomes a spread of its own.
 */
export function insertAfterIndex(spread: number, themed: boolean): number {
  return spreadFirstIndex(spread, themed) + 1;
}

/**
 * The strip's drag: move a whole spread. Pages are renumbered 1…N so `page`
 * stays positional. Returns `pages` itself when the move is refused:
 * out of range, or — in a themed book — anything that would move the S1
 * opening off the front (it is print page 1, on the right, with the mark).
 */
export function reorderSpreads<T extends { page: number }>(pages: T[], from: number, to: number, themed: boolean): T[] {
  const n = spreadCount(pages.length, themed);
  if (from === to || from < 0 || from >= n || to < 0 || to >= n) return pages;
  if (themed && (from === 0 || to === 0)) return pages;
  const spreads: T[][] = [];
  for (let s = 0; s < n; s++) {
    spreads.push(spreadPages(s, themed, pages.length).filter((i): i is number => i != null).map((i) => pages[i]));
  }
  const [moved] = spreads.splice(from, 1);
  spreads.splice(to, 0, moved);
  return spreads.flat().map((p, i) => ({ ...p, page: i + 1 }));
}
